import { ArrowDownLeft, ArrowUpRight } from "lucide-react";

import type { WalletTransactionEntity } from "@/modules/dashboard/cards/types/wallets-api.types";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import {
  isIncomingMovement,
  isManualAdjustment,
  movementAuthor,
  movementDescriptionLabelKey,
  movementStatusLabelKey,
  movementStatusVariant,
  movementTypeLabelKey,
} from "../../utils/cardWalletMovements";

/**
 * What kind of movement this is, and how it ended. Both faces of the list render these, so the badge a reader
 * sees on a phone is the badge they see on a desktop.
 *
 * A manual adjustment is badged from `provider_transaction_id`, never from `transaction_type`: the backend
 * writes `TOPUP` for it, the same value a provider top-up carries.
 */
export function MovementKindBadges({ movement }: { movement: WalletTransactionEntity }) {
  const { t } = useTranslations("cardWallets");
  const manual = isManualAdjustment(movement);
  const typeKey = movementTypeLabelKey(movement.transaction_type);
  const statusKey = movementStatusLabelKey(movement.status);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <ColocaBadge variant={manual ? "primary" : "secondary"}>
        {manual ? t("badge_manual_adjustment") : typeKey ? t(typeKey) : movement.transaction_type}
      </ColocaBadge>
      <ColocaStatusPill variant={movementStatusVariant(movement.status)} size="sm">
        {statusKey ? t(statusKey) : movement.status}
      </ColocaStatusPill>
    </div>
  );
}

/**
 * The movement's own sentence, under the badges.
 *
 * `description` is free text the backend services author by hand, so it arrives untranslated and in whichever
 * language the line that wrote it used. It goes through the same label resolution the badges use; a value the
 * map does not know renders verbatim, which is better than a wrong label and is what every description did
 * before the map existed.
 */
export function MovementDescription({ movement }: { movement: WalletTransactionEntity }) {
  const { t } = useTranslations("cardWallets");
  const key = movementDescriptionLabelKey(movement.description);
  const label = key ? t(key) : movement.description;

  if (!label) return null;

  return <span className="block max-w-80 truncate text-xs text-muted-foreground">{label}</span>;
}

/**
 * The signed amount. The entity stores the magnitude and puts the sign in `direction`, so the sign is
 * reconstructed here and never read off the number. Three channels carry the direction — the arrow, the sign
 * and the tint — so colour is never the only signal.
 */
export function MovementAmount({
  movement,
  size = "sm",
}: {
  movement: WalletTransactionEntity;
  size?: "sm" | "md";
}) {
  const incoming = isIncomingMovement(movement);
  const Icon = incoming ? ArrowDownLeft : ArrowUpRight;
  const magnitude = String(movement.amount).replace(/^-/, "");

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap",
        incoming ? "text-success" : "text-destructive",
      )}
    >
      <Icon className="size-3.5 shrink-0" aria-hidden="true" />
      <ColocaAmountDisplay
        size={size}
        signed
        value={incoming ? magnitude : `-${magnitude}`}
        currency={movement.currency}
      />
    </span>
  );
}

/**
 * Who recorded it.
 *
 * `created_by` is always `null` on a manual adjustment — the backend resolver looks for a metadata key the
 * adjust does not write — so the only name available for free is the signed-in operator's own. The raw uuid
 * is never rendered, and the reason for the gap is said out loud rather than left as an em dash.
 */
export function MovementAuthorCell({
  movement,
  currentUserId,
  variant = "table",
}: {
  movement: WalletTransactionEntity;
  currentUserId: string | null;
  variant?: "table" | "card";
}) {
  const { t } = useTranslations("cardWallets");
  const author = movementAuthor(movement, currentUserId);

  if (author.kind === "named") {
    return (
      <span className="block max-w-44 truncate" title={author.title}>
        {author.label}
      </span>
    );
  }

  if (author.kind === "you") return <span>{t("by_you")}</span>;

  // The card face says the reason as visible text: the table can only express it as a `title`, which a touch
  // screen cannot reach.
  if (variant === "card") {
    return <span className="text-muted-foreground">{t("by_unavailable_card")}</span>;
  }

  return (
    <span className="text-muted-foreground" title={t("by_unavailable_tooltip")}>
      {t("by_unavailable")}
    </span>
  );
}
